// @ts-strict-ignore
import { FileDescriptor, FileState } from 'rainbow-web-sdk/models/common/fileDescriptor';
import { FileDescriptorEventHandlerService } from "rainbow-web-sdk/services/files/fileDescriptorEventHandler.service";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { FileStorageCapabilities, FileStorageExternalStorage, FileStorageService } from 'rainbow-web-sdk/services/files/fileStorage.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { Subject, from } from "rxjs";
import { catchError, filter, mergeMap, toArray } from "rxjs/operators";
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { differenceInSeconds, format, isValid } from 'date-fns';
import { MD5 } from '../utilities';

export const FILE_SERVER_SVC = 'FileServerService';

declare const Buffer: any;
interface UploadRequestData {
    fileDescriptor: FileDescriptor;
    blob: Blob;
    nbOfChunks: number;
    minRange: number;
    maxRange: number;
    index: number;
}

/**
 * Helper class to handle image URLs (to avoid memory issues)
 * @class LocalImage
 */
export class LocalImage {

    /**
     * Image url to be use. e.g. in src attribute.
     * @type {string}
     * @memberof LocalImage
     */
    public url: string;

    /**
     * {@link FileDescriptor} associated with this LocalImage
     * @type {FileDescriptor}
     * @memberof LocalImage
     */
    public fd: FileDescriptor;

    /**
     * Method to be called to release the URL object created when instanciating the class
     * @memberof LocalImage
     */
    public release(): void { URL.revokeObjectURL(this.url); }

    /**
     * Creates an instance of LocalImage.
     * Note: Careful, a URL object is created so {@link release()} is to be called when object is no longer in use
     * @param {FileDescriptor} fd image file descriptor
     * @param {Blob} blob file blob
     * @memberof LocalImage
     */
    constructor(fd: FileDescriptor, blob: Blob) {
        if (blob) { this.url = URL.createObjectURL(blob); }
        this.fd = fd;
    }
}

/**
 * File server constraints (maxChunkSize, maxFileSize) for user
 * @class FileServerCapabilities
 */
export interface FileServerCapabilities {
    /**
     * Threshold from which it's better for clients to follow a download strategy by chunk [octet]
     */
    maxChunkSizeDownload: number;

    /**
     * Threshold from which it's better for clients to follow an upload strategy by chunk [octet]
     */
    maxChunkSizeUpload: number;

    /**
     * Data related with the external fileserver used
     */
    externalStorage?: FileStorageExternalStorage;
}

export interface IFileServerService {
    uploadAFileByChunk(fileDescriptor: FileDescriptor, file: File): Promise<any>;
    uploadFile(fileDescriptor: FileDescriptor): Promise<FileDescriptor>;
}

export class FileServerService extends Service implements IFileServerService {

    readonly ONE_KILOBYTE = 1024;
    readonly ONE_MEGABYTE = 1024 * 1024;
    readonly ONE_GIGABYTE = 1024 * 1024 * 1024;
    readonly FILE_RANGE = 4 * this.ONE_MEGABYTE;
    readonly PARALLEL_REQUEST_NB = 10;

    private _started: boolean = false;
    public get started(): boolean {
        return this._started;
    }

    public set started(value: boolean) {
        this._started = value;
        this.serviceStatusSubject.next(this._started);
    }

    private serviceStatusSubject: Subject<boolean> = new Subject<boolean>();
    private portalURL: string = config.restServerUrl + "/api/rainbow/fileserver/v1.0/files";
    private fileMessageHandlerRef: any;
    private fileDescriptorEventHandler: FileDescriptorEventHandlerService;
    private thumbnailPromises: Promise<Blob>[] = [];
    private thumbnailPreviewPromises: Promise<Blob>[] = [];
    private cache: Cache;
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private fileStorageService: FileStorageService = FileStorageService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private centralizedService: any = null;
    private mainService: MainService = MainService.getInstance();

    // Default value for the size of chunks in the event of a problem retrieving their value on the server side.
    public capabilities: FileServerCapabilities
        = {
            maxChunkSizeDownload: 10485760,
            maxChunkSizeUpload: 10485760
        };

    public static getInstance() {
        return Service.create(FILE_SERVER_SVC, FileServerService);
    }

    public static build() {
        return new FileServerService();
    }

    private constructor() { super(); }

    private async waitTillStarted(): Promise<void> {
        return new Promise((resolve) => {
            if (this.started) { resolve(); }
            else {
                this.serviceStatusSubject.pipe(filter(started => started === true)).subscribe(() => { resolve(); });
            }
        });
    }

    public async start(): Promise<void> {
        try {
            this.logger.info("[FileServerService] === STARTING ===");
            this.portalURL = config.restServerUrl + "/api/rainbow/fileserver/v1.0/files";
            this.centralizedService = this.mainService.inject('CentralizedService');

            // Open the files cache if available
            try { if ('caches' in self) { this.cache = await caches.open('RainbowImageCache'); } } catch (error) { /**/ }

            this.started = true;
            await this.getServerCapabilities();

            // Register to xmpp and call events
            this.attachHandlers();
            this.logger.info("[FileServerService] === STARTED ===");
        }
        catch (error) {
            this.logger.error("[FileServerService] === STARTING FAILURE === " + error.message);
        }
    }

    public async stop() {
        this.logger.info("[FileServerService] === STOPPING ===");
        if (this.started) { this.started = false; }
        this.logger.info("[FileServerService] === STOPPED ===");
    }

    public reconnect(): void {
        this.attachHandlers();
    }

    public attachHandlers() {
        this.removeHandlers();
        this.fileDescriptorEventHandler = FileDescriptorEventHandlerService.create();

        if (!this.fileMessageHandlerRef) {
            this.fileMessageHandlerRef = this.xmppService.addHandler((stanza: any) => {
                setTimeout(() => { this.fileDescriptorEventHandler.onManagementMessageReceived(stanza); }, 250);
                return true;
            }, null, "message", "management");
        }
    }

    private removeHandlers() {
        this.fileDescriptorEventHandler = undefined;
        this.fileMessageHandlerRef = undefined;
    }

    /**
     * Get a "public URL" for a given {@link FileDescriptor} (API request to {@link https://api.openrainbow.org/filestorage/#api-files_server_download-files_getFileTemporaryURL Rainbow File Storage portal - files server download - 2 - Get backend temporary URL})
     * @param {FileDescriptor} fileDescriptor for which we want to retrieve a public URL
     * @returns {Promise<string>} Resolves with the public url
     * @memberof FileServerService
     */
    public async getFileDescriptorPublicUrl(fileDescriptor: FileDescriptor): Promise<string> {
        await this.waitTillStarted();

        if (!fileDescriptor) return null;

        if ((!this.centralizedService?.isDesktopApp()) && fileDescriptor.publicUrl) {
            this.logger.info("[FileServerService]  >getFileDescriptorPublicUrl: publicUrlExpirationDate=" + format(new Date(fileDescriptor.publicUrlExpirationDate), "dd-MM-yyyy_H_mm_ss"));
            this.logger.info("[FileServerService]  >getFileDescriptorPublicUrl: isDateExpired=" + fileDescriptor.isPublicUrlExpired());

            const nowDate = new Date();
            const duration = Math.abs(differenceInSeconds(fileDescriptor.publicUrlExpirationDate, nowDate));
            this.logger.info("[FileServerService] >getFileDescriptorPublicUrl: date expiration=" + duration);
            if (duration > 30 * 90) {
                return fileDescriptor.publicUrl;
            }
        }

        try {
            const serverUrl: URL = new URL(this.portalURL + "/" + fileDescriptor.id + "/temporary-url");
            const headers = this.authService.getRequestHeader();
            headers[`Content-Type`] = `application/json`;
            const response: Response = await fetch(serverUrl.href, { method: 'GET', headers });
            if (!response.ok) throw response;
            const responseData = await response.json();
            fileDescriptor.publicUrl = responseData.data.url;
            const date = new Date(responseData.data.expirationDate);
            if (isValid(date)) {
                fileDescriptor.publicUrlExpirationDate = date;
                this.logger.info("[FileServerService] >getFileDescriptorPublicUrl: Get PublicUrl Success, publicUrlExpirationDate=" + format(new Date(fileDescriptor.publicUrlExpirationDate), "dd-MM-yyyy_H_mm_ss"));
            }

            this.logger.info("[FileServerService] Get PublicUrl Success");
            return fileDescriptor.publicUrl;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getFileDescriptorPublicUrl');
            this.logger.error(`[FileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method creates an URL for a given fileDescriptor
     * @param {FileDescriptor} fileDescriptor for which we retrieve the URL
     * @param {boolean} [thumbnail] whether it is the thumbnail that is requested
     * @param {boolean} [cacheEnabled] whether to use cache or not
     * @returns {Promise<string>} resolves with the URL
     * @memberof FileServerService
     */
    public async getFileDescriptorUrl(fileDescriptor: FileDescriptor, thumbnail: boolean = false, cacheEnabled: boolean = false): Promise<string> {
        try {
            let blob: Blob = null;
            const cacheRequest = new Request(`/rainbowImages/${thumbnail ? 'thumbnail/' : ''}${fileDescriptor.id}`);

            // If cache enabled search Blob in cache
            if (cacheEnabled && this.cache) {
                const cacheResponse = await this.cache.match(cacheRequest);
                if (cacheResponse) { blob = await cacheResponse.blob(); }
            }

            // Blob not found in cache or cache not available get blob from server
            if (!blob) {
                if (thumbnail) {
                    if (fileDescriptor.previewBlob) { blob = fileDescriptor.previewBlob; }
                    else { blob = await this.getBlobThumbnailFromFileDescriptor(fileDescriptor); }
                }
                else {
                    blob = await this.getBlobFromFileDescriptor(fileDescriptor, false, null);
                    // If necessary, get image metadata
                    if (!fileDescriptor.orientation && fileDescriptor.isImage()) {
                        await this.setImageOrientationForFileDescriptor(fileDescriptor, blob).catch(() => { });
                    }
                }

                // If cache enabled then cache Blob
                if (cacheEnabled && this.cache) { this.cache.put(cacheRequest, new Response(blob)); }
            }

            // Create the url
            return URL.createObjectURL(blob);
        }
        catch (error) {
            if (error instanceof Error) {
                throw error;
            } else {
                throw new Error(`getFileDescriptorUrl(${fileDescriptor.id}, ${thumbnail}) -- failure -- ${error}`);
            }
        }
    }

    /**
     * Removes a given {@link FileDescriptor} from cache
     * @param {FileDescriptor} fileDescriptor to be removed from cache
     * @memberof FileServerService
     */
    public removeFileDescriptorFromCache(fileDescriptor: FileDescriptor) {
        if (this.cache) {
            this.cache.delete(`/rainbowImages/thumbnail/${fileDescriptor.id}`);
            this.cache.delete(`/rainbowImages/${fileDescriptor.id}`);
        }
    }

    /**
     * Clears the RainbowImageCache
     * @memberof FileServerService
     */
    public async clearCache() {
        if (this.cache) {
            try {
                await caches.delete('RainbowImageCache');
                this.cache = await caches.open('RainbowImageCache');
            } catch (error) { /* Noop */ }
        }
    }

    /**
     * Method retrieve the {@link LocalImage} of a given file
     * @param {string} fileDescriptorId ID of file
     * @param {boolean} thumbnail whether we want to retrieve the thumbnail
     * @param {boolean} [forced] whether we want to force a server request
     * @returns {Promise<LocalImage>} resolves with the {@link LocalImage}
     * @memberof FileServerService
     */
    public async getLocalImage(fileDescriptorId: string, thumbnail: boolean, forced: boolean = true): Promise<LocalImage> {
        await this.waitTillStarted();

        try {
            // Get the filedescriptor
            let fileDescriptor: FileDescriptor = this.fileStorageService.getFileDescriptorById(fileDescriptorId);
            if (!fileDescriptor) fileDescriptor = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileDescriptorId);
            if (fileDescriptor.fileName.includes('__rboriginal__')) { thumbnail = false; }

            // Determine fileLoader
            let blob: Blob = null;
            if (thumbnail && fileDescriptor.previewBlob && !forced) { blob = fileDescriptor.previewBlob; }
            else if (thumbnail) { blob = await this.getLargeBlobThumbnail(fileDescriptor); }
            else { blob = await this.getBlobFromFileDescriptor(fileDescriptor, false, null); }

            return new LocalImage(fileDescriptor, blob);
        }
        catch (error) {
            this.logger.debug(`[fileServerService] getFileLocalURL -- no file descriptor found with ${fileDescriptorId} + " id.`);
            throw (error);
        }
    }

    private getLargeBlobThumbnail(fileDescriptor: FileDescriptor) {
        const url = fileDescriptor.url + "?thumbnail500=true";
        return this.getBlobFileDescriptorFromUrl(url, fileDescriptor);
    }

    /**
     * Method used to get partial data from server
     * @param {FileDescriptor} fileDescriptor of file to retrieve
     * @param {string} url to use for download
     * @param {number} minRange minRange for download operation
     * @param {number} maxRange maxRange for download operation
     * @param {number} index of partial request
     * @param {boolean} __cancelable cancellable request ?
     * @param {number} currentLoadingRef some kind  of graphical stuff
     * @returns {Promise<{}>} resolves when partial data's download is complete
     * @memberof FileServerService
     */
    private async getPartialDataFromServer(fileDescriptor: FileDescriptor, url: string, minRange: number, maxRange: number, index: number, __cancelable: boolean, currentLoadingRef: number): Promise<{ data: Blob; index: number; }> {
        try {
            await this.waitTillStarted();

            const headers = this.authService.getRequestHeaderWithRange("bytes=" + minRange + "-" + maxRange);
            const response: Response = await fetch(url, { method: 'GET', headers, signal: fileDescriptor.abortTransferController.signal });
            if (!response.ok) { throw (response); }

            const reader = response.body.getReader();
            const contentLength = Number(response.headers.get('Content-Length'));

            let receivedLength = 0;
            const chunks = [];
            let loading: boolean = true;
            while (loading) {
                const { done, value } = await reader.read();

                if (done) {
                    loading = false;
                    continue;
                }

                chunks.push(value);
                receivedLength += value.length;

                if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                    this.logger.info("[FileServerService] This load has been canceled - NTIA");
                    continue;
                }

                const percentChunk = Math.floor((receivedLength * 100) / contentLength);
                try {
                    if (fileDescriptor) {
                        fileDescriptor.chunkPercentArray[index] = percentChunk;
                        fileDescriptor.updateProgressInfo();
                    }
                }
                catch (error) {
                    this.logger.warn(`[FileServerService] getPartialDataFromServer updateProgressInfo error ${error}`);
                }

            }

            const blob = new Blob(chunks);
            return { data: blob, index: index };
        } catch (error) {
            if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
                this.logger.warn(`[FileServerService] ${error.message}`);
                throw error;
            } else {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPartialDataFromServer');
                this.logger.error(`[FileServerService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
    }

    // In order to avoid cyclic dependancy
    public getFileStorageService() { return this.fileStorageService; }

    /**
     * Retrieve Blob of thumbnail preview of a given file descriptor
     * @param {FileDescriptor} fileDescriptor for which we retrieve the blob
     * @returns {Promise<Blob>} resolves with the blob
     * @memberof FileServerService
     */
    private async getBlobThumbnailPreviewFromFileDescriptor(fileDescriptor: FileDescriptor): Promise<Blob> {
        await this.waitTillStarted();

        // Check if a request for this thumbnail preview is already lauched
        if (this.thumbnailPreviewPromises[fileDescriptor.id]) {
            this.logger.info("[FileServerService] getBlobThumbnailFromFileDescriptor preview for " + fileDescriptor.id + " already lauched");
            return this.thumbnailPreviewPromises[fileDescriptor.id];
        }

        const url = fileDescriptor.url + "?thumbnail500=true";
        this.thumbnailPreviewPromises[fileDescriptor.id] = new Promise((resolve, reject) => {
            // Get the thumbnail blob
            this.getBlobFileDescriptorFromUrl(url, fileDescriptor).then((blob) => {
                //update file descriptor
                fileDescriptor = this.fileStorageService.getFileDescriptorById(fileDescriptor.id);
                fileDescriptor.previewBlob = blob;

                //the thumbnail preview is now downloaded
                fileDescriptor.previewDownloaded = true;
                fileDescriptor.previewBlobSubject.next(fileDescriptor.previewBlob);

                delete this.thumbnailPreviewPromises[fileDescriptor.id];
                resolve(blob);
            }).catch((error) => {
                delete this.thumbnailPreviewPromises[fileDescriptor.id];
                reject(error);
            });
        });
        return this.thumbnailPreviewPromises[fileDescriptor.id];
    }

    /**
     * Retrieve Blob of thumbnail of a given file descriptor
     * @param {FileDescriptor} fileDescriptor for which we retrieve the blob
     * @returns {Promise<Blob>} resolves with the blob
     * @memberof FileServerService
     */
    public async getBlobThumbnailFromFileDescriptor(fileDescriptor: FileDescriptor): Promise<Blob> {

        await this.waitTillStarted();

        if (fileDescriptor.thumbnail.isThumbnailAvailable() || fileDescriptor.isImage() || fileDescriptor.recordingFile) {
            // Forge the thumbnail url
            let url = fileDescriptor.url;

            //download real thumbnail
            if (fileDescriptor.thumbnail.isThumbnailAvailable()) {
                return this.getBlobThumbnailPreviewFromFileDescriptor(fileDescriptor);
            }

            // Check if a request for this full image is already lauched
            if (this.thumbnailPromises[fileDescriptor.id]) {
                this.logger.info("[FileServerService] getBlobThumbnailFromFileDescriptor full image update for " + fileDescriptor.id + " already lauched");
                return this.thumbnailPromises[fileDescriptor.id];
            }
            if (fileDescriptor.uploadedDate) {
                url += "?update=" + MD5(fileDescriptor.uploadedDate.toString());
            }

            this.thumbnailPromises[fileDescriptor.id] = new Promise((resolve, reject) => {
                // Get the thumbnail blob
                this.getBlobFileDescriptorFromUrl(url, fileDescriptor).then(async (blob) => {

                    //update file descriptor
                    if (!fileDescriptor.recordingFile) {
                        fileDescriptor = this.fileStorageService.getFileDescriptorById(fileDescriptor.id);
                    } else {
                        fileDescriptor = await this.fileStorageService.retrieveOneFileDescriptor(fileDescriptor.id);
                    }
                    if (fileDescriptor) {
                        if (!fileDescriptor.previewBlob) fileDescriptor.previewBlob = blob;

                        // Orientation
                        if (!fileDescriptor.orientation && fileDescriptor.isImage()) {
                            await this.setImageOrientationForFileDescriptor(fileDescriptor, blob).catch(() => { });
                        }
                        fileDescriptor.previewBlobSubject.next(fileDescriptor.previewBlob);

                        delete this.thumbnailPromises[fileDescriptor.id];
                        resolve(blob);

                    }
                }).catch((error) => {
                    delete this.thumbnailPromises[fileDescriptor.id];
                    reject(error);
                });
            });
            return this.thumbnailPromises[fileDescriptor.id];
        }
        throw (new Error("No available thumbnail"));
    }

    /**
     * Helper method to call getPartialDataFromServer and handle its response
     * @private
     * @param {FileDescriptor} fileDescriptor of file to retrieve
     * @param {string} url to use for download
     * @param {number} minRange minRange for download operation
     * @param {number} maxRange maxRange for download operation
     * @param {number} index of partial request
     * @param {boolean} cancelable cancellable request ?
     * @param {number} currentLoadingRef some kind  of graphical stuff
     * @param {any[]} blobArray to fill with response
     * @param {Function} [actionOnPartialResponse] what to do when partial response is received
     * @returns {*}  {Promise<any>}
     * @memberof FileServerService
     */
    private async callAndProcessGetPartialDataFromServer(fileDescriptor: FileDescriptor, url: string, minRange: number, maxRange: number, index: number, cancelable: boolean, currentLoadingRef: number, blobArray: any[], actionOnPartialResponse: Function = null): Promise<any> {
        try {
            const response = await this.getPartialDataFromServer(fileDescriptor, url, minRange, maxRange, index, cancelable, currentLoadingRef);
            if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                this.logger.info("[FileServerService] This load has been canceled - NTIA");
                return null;
            }

            try {
                fileDescriptor.updateProgressInfo();
            }
            catch (error) {
                this.logger.warn(`[FileServerService] callAndProcessGetPartialDataFromServer updateProgressInfo error ${error}`);
            }

            this.logger.info("[FileServerService] getBlobFromFileDescriptor : chunk" + index + " downloaded");
            this.logger.debug("[FileServerService] refreshFilePercent: chunkPerformedPercent=" + fileDescriptor.chunkPerformedPercent);

            blobArray[response['index']] = response['data'];
            this.writeAvailableChunksInDisk(fileDescriptor, blobArray);

            if (actionOnPartialResponse) {
                await actionOnPartialResponse(response, fileDescriptor.id);
                return response['data'];
            }
            return response;
        } catch (error) {
            this.logger.info("[FileServerService] error on chunk download=" + error);
            throw (error);

        }
    }

    /**
     * Method retrieve the blob of a given file descriptor
     * @param {FileDescriptor} fileDescriptor whose blob we want to retrieve
     * @param {boolean} [cancelable] whether action is ancellable
     * @param {Function} [actionOnPartialResponse] what to do when partial response is received
     * @returns {Promise<Blob>} resolves with the retieved blob
     * @memberof FileServerService
     */
    public async getBlobFromFileDescriptor(fileDescriptor: FileDescriptor, cancelable: boolean = true, actionOnPartialResponse: Function = null): Promise<Blob> {
        await this.waitTillStarted();

        if (!fileDescriptor) {
            const err = new Error(`[FileServerService] getBlobFromFileDescriptor : missing file descriptor`);
            this.logger.warn(err.message);
            throw err;
        }

        this.logger.info("[FileServerService] getBlobFromFileDescriptor: " + fileDescriptor.id);

        if (fileDescriptor.state !== FileState.UPLOADED) {
            const err = new Error(`[FileServerService] getBlobFromFileDescriptor : file is not in uploaded state=${fileDescriptor.state}`);
            this.logger.warn(err.message);
            throw err;
        }

        let url = fileDescriptor.url;
        if (fileDescriptor.uploadedDate) url += "?update=" + MD5(fileDescriptor.uploadedDate.toString());

        const chunkSize = this.capabilities.maxChunkSizeDownload;

        if (Boolean(this.capabilities.maxChunkSizeDownload) && fileDescriptor.size !== 0 && fileDescriptor.size > chunkSize) {
            const blobArray = [];
            const requestDataArray: any[] = [];
            fileDescriptor.state = FileState.DOWNLOADING;
            fileDescriptor.chunkTotalNumber = Math.ceil(fileDescriptor.size / chunkSize);
            fileDescriptor.chunkPerformedPercent = 0;
            fileDescriptor.chunkWrittenInDisk = -1;
            fileDescriptor.chunkPercentArray = new Array(fileDescriptor.chunkTotalNumber); // TODO
            fileDescriptor.chunkPercentArray.fill(0);
            fileDescriptor.abortTransferController = new AbortController();

            // Update loading ref array with new reference set with max ref value + 1
            // If loading ref array was empty, reference starts from 1
            const maxLoadingRef = (fileDescriptor.loadingRef.length) ? Math.max(...fileDescriptor.loadingRef) : 0;
            const currentLoadingRef = maxLoadingRef + 1;
            fileDescriptor.loadingRef.push(currentLoadingRef);

            try { fileDescriptor.updateProgressInfo(); }
            catch (error) { this.logger.warn(`[FileServerService] getBlobFromFileDescriptor updateProgressInfo error ${error}`); }

            this.logger.info("[FileServerService] getBlobFromFileDescriptor: need to download " + fileDescriptor.chunkTotalNumber + " chunks");

            for (let i = 0; i < fileDescriptor.chunkTotalNumber; i++) {
                // eslint-disable-next-line prefer-const
                let minRange = i * chunkSize;
                let maxRange = (i + 1) * chunkSize - 1;
                maxRange = (maxRange < fileDescriptor.size) ? maxRange : fileDescriptor.size;
                requestDataArray.push({
                    fileDescriptor: fileDescriptor,
                    url: url,
                    minRange: minRange,
                    maxRange: maxRange,
                    i: i,
                    cancelable: cancelable,
                    currentLoadingRef: currentLoadingRef,
                    blobArray: blobArray,
                    actionOnPartialResponse: actionOnPartialResponse
                });
            }
            const parallelRequestNumber = fileDescriptor.externalStorage ? 1 : this.PARALLEL_REQUEST_NB;

            return new Promise((resolve, reject) => {
                fileDescriptor.fileTransferSubscription = from(requestDataArray)
                    .pipe(mergeMap(requestData =>
                        this.callAndProcessGetPartialDataFromServer(
                            requestData.fileDescriptor,
                            requestData.url,
                            requestData.minRange,
                            requestData.maxRange,
                            requestData.i,
                            requestData.cancelable,
                            requestData.currentLoadingRef,
                            requestData.blobArray,
                            requestData.actionOnPartialResponse), parallelRequestNumber))
                    .pipe(catchError(async (errorResponse) => {
                        if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                            this.logger.info("[FileServerService] This load has been canceled - NTIA");
                            return;
                        }

                        fileDescriptor.setState(FileState.UPLOADED);
                        fileDescriptor.chunkTotalNumber = 0;
                        fileDescriptor.chunkPerformedPercent = 0;

                        if (errorResponse instanceof DOMException) {
                            if (errorResponse.code === DOMException.ABORT_ERR) {
                                this.logger.warn(`[FileServerService] ${errorResponse.message}`);
                                reject(errorResponse);
                            }
                        }

                        const errorInfo = await this.errorHelperService.getErrorInfo(errorResponse, 'getFileDescriptorPublicUrl');
                        const translatedErrorMessage = errorInfo.error?.translatedMessage ? errorInfo.error.translatedMessage : errorInfo.message;

                        this.logger.error("[FileServerService] error= " + translatedErrorMessage);
                        reject(errorInfo);
                    }))
                    .pipe(toArray())
                    .subscribe(async () => {
                        if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                            this.logger.info("[FileServerService] This load has been canceled - NTIA");
                            return;
                        }

                        this.logger.info("[FileServerService] getBlobFromFileDescriptor: promisesCompletion");

                        //console.timeEnd("[FILEOPTIM_TEST] File Download");
                        const blob = new Blob(blobArray, { type: fileDescriptor.typeMIME });
                        this.logger.info("[FileServerService] getBlobFromFileDescriptor success");
                        fileDescriptor.setState(FileState.UPLOADED);
                        fileDescriptor.chunkTotalNumber = 0;
                        fileDescriptor.chunkPerformedPercent = 0;
                        try { fileDescriptor.updateProgressInfo(); }
                        catch (error) { this.logger.warn(`[FileServerService] getBlobFromFileDescriptor updateProgressInfo error ${error}`); }

                        await this.writeAvailableChunksInDisk(fileDescriptor, blobArray);
                        if (fileDescriptor.writeStream) {
                            this.logger.info("[FileServerService] Close WriteStream");
                            fileDescriptor.writeStream.end();
                        }

                        resolve(blob);
                    });
            });
        }

        try {
            // We change state only in this case for getBlobFileDescriptorFromUrl
            // to prevent changing state for preview download
            fileDescriptor.state = FileState.DOWNLOADING;
            const blob = await this.getBlobFileDescriptorFromUrl(url, fileDescriptor);
            return blob;
        }
        catch (error) {
            fileDescriptor.state = FileState.UPLOADED;
            this.logger.info("[FileServerService] arrayPromise : error " + error.message);
            throw error;
        }
    }

    /**
     * Write the given blob's ArrayBuffer to fileDescriptor
     * @param {FileDescriptor} fd to fill
     * @param {ArrayBuffer[]} blobArray of data to write
     * @returns {void}
     * @memberof FileServerService
     */
    private async writeAvailableChunksInDisk(fd: FileDescriptor, blobArray: ArrayBuffer[]): Promise<void> {
        this.logger.debug("[FileServerService] >writeAvailableChunksInDisk");
        if (!fd.writeStream) {
            // Not a WriteStream - skip
            return;
        }

        const start = fd.chunkWrittenInDisk + 1;
        this.logger.debug("[FileServerService] >writeAvailableChunksInDisk : We start at " + start);

        for (let index = start; index < blobArray.length; index++) {
            if (blobArray[index]) {
                this.logger.debug("[FileServerService] >writeAvailableChunksInDisk : Blob " + index + " available");
                fd.chunkWrittenInDisk = index;
                const arrayBuffer = await (blobArray[index] as any).arrayBuffer();
                const buffer = Buffer.from(arrayBuffer);
                fd.writeStream.write(buffer);
                blobArray[index] = null;
            } else {
                this.logger.debug("[FileServerService] >writeAvailableChunksInDisk : Blob " + index + " NOT available");
                break;
            }
        }
    }

    /**
     * Method creates blob from a file retrieved from server
     * @param {string} url server url for request
     * @param {FileDescriptor} fileDescriptor for which we retrieve the blob
     * @returns {Promise<Blob>} resolves with the Blob created from data received from server
     * @memberof FileServerService
     */
    public async getBlobFileDescriptorFromUrl(url: string, fileDescriptor: FileDescriptor): Promise<Blob> {
        await this.waitTillStarted();

        this.logger.debug("[FileServerService] >getBlobFileDescriptorFromUrl: " + url);

        fileDescriptor.chunkTotalNumber = 1;
        fileDescriptor.chunkPerformedPercent = 0;
        fileDescriptor.chunkPercentArray = new Array(fileDescriptor.chunkTotalNumber);
        fileDescriptor.chunkPercentArray.fill(0);

        // Update loading ref array with new reference set with max ref value + 1
        // If loading ref array was empty, reference starts from 1
        const maxLoadingRef = (fileDescriptor.loadingRef.length) ? Math.max(...fileDescriptor.loadingRef) : 0;
        const currentLoadingRef = maxLoadingRef + 1;
        fileDescriptor.loadingRef.push(currentLoadingRef);

        try {
            fileDescriptor.updateProgressInfo();
        }
        catch (error) {
            this.logger.warn(`[FileServerService] getBlobFileDescriptorFromUrl updateProgressInfo error ${error}`);
        }

        try {
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers, signal: fileDescriptor.abortTransferController.signal });
            if (!response.ok) { throw (response); }

            const reader = response.body.getReader();
            const contentLength = Number(response.headers.get('Content-Length'));

            let receivedLength = 0;
            const chunks = [];
            let loading: boolean = true;
            while (loading) {
                const { done, value } = await reader.read();

                if (done) {
                    loading = false;
                    continue;
                }

                chunks.push(value);
                receivedLength += value.length;

                //this.logger.info(`[FileServerService] Received ${receivedLength} of ${contentLength}`);

                if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                    this.logger.info("[FileServerService] This load has been canceled - NTIA");
                    continue;
                }

                const percentChunk = Math.floor((receivedLength * 100) / contentLength);
                if (fileDescriptor) {
                    fileDescriptor.chunkPercentArray[0] = percentChunk;
                    try {
                        fileDescriptor.updateProgressInfo();
                    }
                    catch (error) {
                        this.logger.warn(`[FileServerService] getBlobFileDescriptorFromUrl updateProgressInfo error ${error}`);
                    }
                }
            }

            if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                this.logger.info("[FileServerService] This load has been canceled - NTIA");
                return null;
            }

            const blob = new Blob(chunks, { type: fileDescriptor.typeMIME });

            fileDescriptor.state = FileState.UPLOADED;
            fileDescriptor.chunkPerformedPercent = 0;
            try {
                fileDescriptor.updateProgressInfo();
            }
            catch (error) {
                this.logger.warn(`[FileServerService] getBlobFileDescriptorFromUrl updateProgressInfo error ${error}`);
            }

            this.logger.debug("[FileServerService] getBlobFileDescriptorFromUrl success");
            return blob;

        } catch (error) {
            if (!fileDescriptor.loadingRef.includes(currentLoadingRef)) {
                this.logger.info("[FileServerService] This load has been canceled - NTIA");
                return null;
            }
            fileDescriptor.state = FileState.UPLOADED;

            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getBlobFileDescriptorFromUrl');
            this.logger.error(`[FileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method retrieves an URL object from an url
     * @param {string} id [required] the ID of the image file
     * @returns {Promise<any>} Object containing the orientation and the Object URL
     * @memberof FileServerService
     */
    public async getBlobUrlAndOrientationByFileDescriptorId(id: string): Promise<any> {
        const fileDescriptor = this.fileStorageService.getFileDescriptorById(id);
        if (fileDescriptor) {
            try {
                const blob = await this.getBlobFromFileDescriptor(fileDescriptor, false, null);
                const result = { id, blob, blobUrl: URL.createObjectURL(blob) };//if we've already calculated the orientation
                if (fileDescriptor.orientation) {
                    result["orientation"] = fileDescriptor.orientation;
                    fileDescriptor.blobOrientationSubject.next(result);
                    return result;
                }
                //should calculate the orientation
                try {
                    const orientation = await this.setImageOrientationForFileDescriptor(fileDescriptor, blob);
                    result["orientation"] = orientation;
                } catch (error) {
                    // NoOp
                }
                fileDescriptor.blobOrientationSubject.next(result);
                return result;
            } catch (error) {
                this.logger.error("[FileServerService] Error on getBlobFromFileDescriptor");
                throw (error);
            }
        }
        else {
            this.logger.error("[FileServerService] getBlobUrlAndOrientationById no file descriptor found for ID " + id);
            throw (new Error("[FileServerService] getBlobUrlAndOrientationById no file descriptor found"));
        }
    }

    /**
     * Method to read EXIF data for image to extract the orientation and store it
     * @param {FileDescriptor} fileDescriptor of image to read
     * @param {Blob} blob of file to analyze
     * @returns {Promise<any>} : return the orientation value if available
     */
    public setImageOrientationForFileDescriptor(fileDescriptor: FileDescriptor, blob: Blob): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!fileDescriptor?.isImage()) { reject(); return; }

            const reader = new FileReader();
            reader.onload = (evt: any) => {
                fileDescriptor.orientation = 1;
                const arrayBuffer = evt.target.result;
                const view = new DataView(arrayBuffer);
                if (view.getUint16(0, false) !== 0xFFD8) { reject(); return; }

                const length = view.byteLength;
                let offset = 2;

                while (offset < length) {
                    if (view.getUint16(offset + 2, false) <= 8) { reject(); return; }
                    const marker = view.getUint16(offset, false);
                    offset += 2;
                    if (marker === 0xFFE1) {
                        if (view.getUint32(offset += 2, false) !== 0x45786966) { reject(); return; }

                        const little = view.getUint16(offset += 6, false) === 0x4949;
                        offset += view.getUint32(offset + 4, little);
                        const tags = view.getUint16(offset, little);
                        offset += 2;
                        for (let i = 0; i < tags; i++) {
                            if (view.getUint16(offset + (i * 12), little) === 0x0112) {
                                const orientation = view.getUint16(offset + (i * 12) + 8, little);
                                fileDescriptor.orientation = orientation;
                                resolve(orientation);
                                return;
                            }
                        }
                    }
                    else if ((marker & 0xFF00) !== 0xFF00) {
                        break;
                    }
                    else {
                        offset += view.getUint16(offset, false);
                    }
                }

                reject();
                return;
            };
            reader.readAsArrayBuffer(blob);
        });
    }

    /**
     * Method sends data to server using range request mecanism (RFC7233)
     * Note: this implementation uses fetch api. Thus, upload progress is not available.
     * @param {UploadRequestData} uploadRequestData to be sent
     * @returns {Promise<{}>} file descriptor data received as response from server or http error response
     * @memberof FileServerService
     */
    private async sendPartialDataToServer(uploadRequestData: UploadRequestData): Promise<any> {
        await this.waitTillStarted();

        const fileDescriptor: FileDescriptor = uploadRequestData.fileDescriptor;

        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            let serverUrl: URL = new URL(this.portalURL + "/" + fileDescriptor.id);
            let headers = this.authService.getPostHeader("application/octet-stream");

            if (fileDescriptor.chunkTotalNumber > 1) {
                serverUrl = new URL(this.portalURL + "/" + fileDescriptor.id + "/parts/" + uploadRequestData.index);
                headers = this.authService.getPostHeaderWithRange("bytes " + uploadRequestData.minRange + "-" + uploadRequestData.maxRange + "/" + fileDescriptor.size, "application/octet-stream");
            }
            request.open('PUT', serverUrl.href);

            Object.entries<string>(headers).map(([headerName, headerValue]) => {
                request.setRequestHeader(headerName, headerValue);
            });

            fileDescriptor.abortXMLHttpRequestArray.push(() => {
                request.abort();
            });

            // upload progress event
            request.upload.addEventListener('progress', (eventData) => {
                // upload progress as percentage
                const percentCompleted = (eventData.loaded / eventData.total) * 100;
                fileDescriptor.chunkPercentArray[uploadRequestData.index] = percentCompleted;
                try {
                    fileDescriptor.updateProgressInfo();
                }
                catch (error) {
                    this.logger.warn(`[FileServerService] sendPartialDataToServer progress updateProgressInfo error ${error}`);
                }
            });

            // handle success
            request.onload = () => {
                if (request.status !== 200) {
                    const error: any = new Error(request.statusText);
                    error.errorDetailsCode = request.status;
                    if (request.responseText) {
                        const response = JSON.parse(request.responseText);
                        if (response?.errorCode) error.errorCode = response.errorCode;
                        if (response?.errorDetailsCode) error.errorDetailsCode = response.errorDetailsCode;
                        if (response?.errorMsg) error.errorMsg = response.errorMsg;
                        if (response?.errorDetails) error.errorDetails = response.errorDetails;
                    }
                    reject(error);
                }

                const chunkResponse = JSON.parse(request.responseText).data;
                this.logger.info("[FileServerService] sendPartialDataToServer success");
                try {
                    fileDescriptor.chunkPercentArray[uploadRequestData.index] = 100;
                    fileDescriptor.updateProgressInfo();
                }
                catch (error) {
                    this.logger.warn(`[FileServerService] sendPartialDataToServer updateProgressInfo error ${error}`);
                }

                resolve(chunkResponse);
            };

            // handle error
            request.onerror = async (error) => {

                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendPartialDataToServer');
                this.logger.error(`[FileServerService] ${errorInfo.message}`);
                reject(errorInfo.error);
            };

            // handle abort
            request.addEventListener('abort', () => {
                this.logger.warn(`[FileServerService] Request aborted for ${fileDescriptor.id} - part: ${uploadRequestData.index}`);
                resolve(null); // gracefully resolves abortion
            });

            request.send(uploadRequestData.blob);
        });
    }

    /**
     * Method to upload a file by chunk
     * @param {FileDescriptor} fileDescriptor of file to upoad
     * @param {File} file to upload
     * @returns {Promise<any>} resolves with the fileDescriptor uploaded
     * @memberof FileServerService
     */
    public async uploadAFileByChunk(fileDescriptor: FileDescriptor, file: File): Promise<any> {
        await this.waitTillStarted();

        const chunkSize = this.capabilities.maxChunkSizeUpload;
        const chunkTotalNumber = Math.ceil(file.size / chunkSize);
        this.logger.info(`[FileServerService] uploadAFileByChunk -- chunkTotalNumber = ${chunkTotalNumber}`);

        const requestDataArray: UploadRequestData[] = [];

        fileDescriptor.chunkTotalNumber = chunkTotalNumber;
        fileDescriptor.chunkPerformedPercent = 0;
        fileDescriptor.chunkPercentArray = new Array(fileDescriptor.chunkTotalNumber);
        fileDescriptor.chunkPercentArray.fill(0);
        fileDescriptor.state = FileState.UPLOADING;

        for (let index = 0; index < chunkTotalNumber; index++) {
            const minRange = index * chunkSize;
            let maxRange = (index + 1) * chunkSize - 1;
            maxRange = (maxRange < file.size) ? maxRange : file.size;
            const blob = file.slice(minRange, maxRange + 1);

            requestDataArray.push({ fileDescriptor: fileDescriptor, blob: blob, minRange: minRange, maxRange: maxRange, index: index, nbOfChunks: chunkTotalNumber });
        }

        const requestDataArray$ = from(requestDataArray);
        const parallelRequestNumber = fileDescriptor.externalStorage ? 1 : this.PARALLEL_REQUEST_NB;

        return new Promise((resolve, reject) => {
            fileDescriptor.fileTransferSubscription = requestDataArray$
                .pipe(mergeMap(requestData => this.sendPartialDataToServer(requestData), parallelRequestNumber))
                .pipe(catchError(async (errorResponse) => {
                    try {
                        const errorInfo = await this.errorHelperService.getErrorInfo(errorResponse, 'uploadAFileByChunk');
                        const translatedErrorMessage = errorInfo.error?.translatedMessage ? errorInfo.error.translatedMessage : errorInfo.message;
                        this.logger.error("[FileServerService] error= " + translatedErrorMessage);
                        reject(errorInfo);
                        //throw errorInfo;
                    } catch (__error) {
                        // If we didn't manage to handle the error, we reject it as it is
                        reject(errorResponse);
                        throw errorResponse;

                    }
                }))
                .pipe(toArray())
                .subscribe(async () => {
                    try {
                        if (fileDescriptor.chunkTotalNumber > 1) {
                            const serverUrl: URL = new URL(this.portalURL + "/" + fileDescriptor.id + "/parts/end");
                            const headers = this.authService.getPostHeader("application/octet-stream");

                            const response: Response = await fetch(serverUrl.href, { method: 'PUT', headers });
                            if (!response.ok) { throw (response); }

                        }

                        this.logger.info("[FileServerService] uploadAFileByChunk success");
                        fileDescriptor.setState(FileState.UPLOADED);
                        fileDescriptor.chunkPerformed = 0;
                        fileDescriptor.chunkTotalNumber = 0;
                        fileDescriptor.chunkPerformedPercent = 0;
                        fileDescriptor.fileToSend = null;
                        this.fileStorageService.removeUploadingFileDescriptor(fileDescriptor);
                        // this.fileStorageService.orderDocuments();
                        this.fileStorageService.retrieveUserConsumption();
                        resolve(fileDescriptor);

                    } catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, 'uploadAFileByChunk');
                        this.logger.error(`[FileServerService] ${errorInfo.message}`);
                        throw errorInfo.error;
                    }
                }, () => {
                    this.fileStorageService.removeUploadingFileDescriptor(fileDescriptor);
                    this.fileStorageService.retrieveUserConsumption();

                });
        });
    }

    /**
     * Method to upload a file
     * @param {FileDescriptor} fileDescriptor of file to upload . Note: attribute "fileToSend" of fileDescriptor will be uploaded
     * @returns {Promise<any>} resolves with the fileDescriptor uploaded
     * @memberof FileServerService
     */
    public async uploadFile(fileDescriptor: FileDescriptor): Promise<FileDescriptor> {
        await this.waitTillStarted();

        const file: File = fileDescriptor.fileToSend;
        const chunkSize = this.capabilities.maxChunkSizeUpload;
        const chunkTotalNumber = Math.ceil(file.size / chunkSize);
        this.logger.info(`[FileServerService] uploadFile -- chunkTotalNumber = ${chunkTotalNumber}`);

        const requestDataArray: UploadRequestData[] = [];

        fileDescriptor.chunkTotalNumber = chunkTotalNumber;
        fileDescriptor.chunkPerformedPercent = 0;
        fileDescriptor.chunkPercentArray = new Array(fileDescriptor.chunkTotalNumber);
        fileDescriptor.chunkPercentArray.fill(0);
        fileDescriptor.state = FileState.UPLOADING;

        for (let index = 0; index < chunkTotalNumber; index++) {
            const minRange = index * chunkSize;
            let maxRange = (index + 1) * chunkSize - 1;
            maxRange = (maxRange < file.size) ? maxRange : file.size;
            const blob = file.slice(minRange, maxRange + 1);

            requestDataArray.push({ fileDescriptor: fileDescriptor, blob: blob, minRange: minRange, maxRange: maxRange, index: index, nbOfChunks: chunkTotalNumber });
        }

        const requestDataArray$ = from(requestDataArray);
        const parallelRequestNumber = fileDescriptor.externalStorage ? 1 : this.PARALLEL_REQUEST_NB;

        return new Promise((resolve, reject) => {
            fileDescriptor.fileTransferSubscription = requestDataArray$
                .pipe(mergeMap(requestData => this.sendPartialDataToServer(requestData), parallelRequestNumber))
                .pipe(catchError(async (errorResponse) => {

                    try {
                        const errorInfo = await this.errorHelperService.getErrorInfo(errorResponse, 'uploadFile');
                        const translatedErrorMessage = errorInfo.error?.translatedMessage ? errorInfo.error.translatedMessage : errorInfo.message;
                        this.logger.error("[FileServerService] error= " + translatedErrorMessage);
                        reject(errorInfo);
                        //throw errorInfo;
                    } catch (__error) {
                        // If we didn't manage to handle the error, we reject it as it is
                        reject(errorResponse);
                        throw errorResponse;

                    }
                }))
                .pipe(toArray())
                .subscribe(async () => {
                    try {
                        if (fileDescriptor.chunkTotalNumber > 1) {
                            const serverUrl: URL = new URL(this.portalURL + "/" + fileDescriptor.id + "/parts/end");
                            const headers = this.authService.getPostHeader("application/octet-stream");

                            const response: Response = await fetch(serverUrl.href, { method: 'PUT', headers });
                            if (!response.ok) { throw (response); }

                        }

                        this.logger.info("[FileServerService] uploadFile success");
                        fileDescriptor.setState(FileState.UPLOADED);
                        fileDescriptor.chunkPerformed = 0;
                        fileDescriptor.chunkTotalNumber = 0;
                        fileDescriptor.chunkPerformedPercent = 0;
                        fileDescriptor.fileToSend = null;

                        this.fileStorageService.removeUploadingFileDescriptor(fileDescriptor);
                        // this.fileStorageService.orderDocuments();
                        this.fileStorageService.retrieveUserConsumption();
                        resolve(fileDescriptor);

                    } catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, 'uploadFile');
                        this.logger.error(`[FileServerService] ${errorInfo.message}`);
                        throw errorInfo.error;
                    }
                }, () => {
                    this.fileStorageService.removeUploadingFileDescriptor(fileDescriptor);
                    this.fileStorageService.retrieveUserConsumption();
                });
        });
    }

    /**
     * Method cancels the transfer for a given fileDescriptor
     * @param {string} fileDescriptorId ID of fileDescriptor transfer to cancel
     * @memberof FileServerService
     */
    public cancelCurrentFiletransfer(fileDescriptorId: string) {
        const fileDesc = this.fileStorageService.getFileDescriptorById(fileDescriptorId);
        if (fileDesc) {
            if (fileDesc.abortTransferController) fileDesc.abortTransferController.abort();
            if (fileDesc.abortXMLHttpRequestArray?.length) fileDesc.abortXMLHttpRequestArray.map((func) => { func(); });
            if (fileDesc.fileTransferSubscription) { fileDesc.fileTransferSubscription.unsubscribe(); }
            if (fileDesc.state === FileState.UPLOADING) fileDesc.state = FileState.NOT_UPLOADED;
            else if (fileDesc.state === FileState.DOWNLOADING) fileDesc.state = FileState.UPLOADED;
            fileDesc.progressSubject.next({});
        }
    }

    /**
     * This API advice client about file server constraints (maxChunkSize, maxFileSize). (API request to {@link https://api.openrainbow.org/filestorage/#api-files_server_capabilities Rainbow File Storage portal - files server capabilities - Capabilities})
     * @description
     *      Method retrieves user quota (capabilities) for user
     * @returns {Promise<FileServerCapabilities>} resolves with a {@link FileServerCapabilities} object
     * @memberof FileServerService
     */
    public async getServerCapabilities(): Promise<FileServerCapabilities> {
        try {
            await this.waitTillStarted();
            const serverUrl: URL = new URL(config.restServerUrl + "/api/rainbow/fileserver/v1.0/capabilities");
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(serverUrl.href, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            this.capabilities = (await response.json()).data;

            if (this.capabilities.externalStorage) {
                this.fileStorageService.externalStorage = this.capabilities.externalStorage;
                this.logger.info(`[FileServerService] getServerCapabilities -- external storage server available ${this.capabilities.externalStorage.type}`);
            }
            this.logger.info(`[FileServerService] getServerCapabilities -- success`);
            return this.capabilities;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerCapabilities');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getFileDescriptorCapability(fileDescriptor: FileDescriptor, capability: FileStorageCapabilities): boolean {

        switch (capability) {
            case FileStorageCapabilities.THUMBNAILS:
                return fileDescriptor.isThumbnailPossible();
            case FileStorageCapabilities.SAVE:
                return !fileDescriptor.externalStorage;
            case FileStorageCapabilities.TRANSFERT:
                return !fileDescriptor.externalStorage;
            default: return false;
        }
    }
}
